import React, { useState } from 'react';
import { cfLogo } from '../../assets';
import { setCookie } from '../../helper';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaUserShield } from 'react-icons/fa';

const BRAND_GRADIENT =
  'linear-gradient(to right, rgb(190 49 199), rgb(170 46 226), rgb(91 146 245), rgb(50 197 255))';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      // Fetch admin credentials from server for the provided email
      const resp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/credentials?email=${encodeURIComponent(email)}`);
      if (!resp.ok) throw new Error('Network error');
      const result = await resp.json();
      const admin = result?.admin;
      if (result?.success && admin && admin.email === email && admin.password === password) {
        // mark as admin in session and set cookie
        sessionStorage.setItem('userType', 'admin');
        setCookie('user', 'causalfunnel-prod@causalfunnel.com', 3);
        navigate('/');
      } else {
        setError('Invalid admin credentials');
      }
    } catch (err) {
      console.error('Admin login error', err);
      setError('Failed to verify admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="login__page min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-100 via-neutral-200 to-neutral-300 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="relative overflow-hidden rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
          {/* Top gradient accent bar */}
          <div className="h-1.5 w-full" style={{ background: BRAND_GRADIENT }} />

          <div className="px-8 py-10 text-neutral-800 dark:text-neutral-200">
            {/* Logo + heading */}
            <div className="text-center">
              <img className="mx-auto w-40 mb-6" src={cfLogo} alt="CausalFunnel logo" />
              <div
                className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg"
                style={{ background: BRAND_GRADIENT }}
              >
                <FaUserShield className="text-2xl" />
              </div>
              <h3 className="text-xl font-semibold">Admin Login</h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Sign in with your admin credentials
              </p>
            </div>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              {/* Email */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600 dark:text-neutral-300">
                  Email
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
                    <FaEnvelope className="text-sm" />
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-sm text-neutral-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 dark:border-neutral-600 dark:bg-neutral-700/60 dark:text-neutral-100 dark:placeholder:text-neutral-400"
                    placeholder="admin@example.com"
                    autoComplete="off"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600 dark:text-neutral-300">
                  Password
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
                    <FaLock className="text-sm" />
                  </span>
                  <input
                    type={passwordVisible ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-10 text-sm text-neutral-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 dark:border-neutral-600 dark:bg-neutral-700/60 dark:text-neutral-100 dark:placeholder:text-neutral-400"
                    placeholder="••••••••"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
                    aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                  >
                    {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center rounded-lg px-6 py-2.5 text-sm font-medium uppercase tracking-wide text-white shadow-md transition duration-150 ease-in-out hover:shadow-lg focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                style={{ background: BRAND_GRADIENT }}
              >
                {loading ? (
                  <svg
                    className="h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <span>Login</span>
                )}
              </button>
            </form>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-neutral-500 dark:text-neutral-400">
          Restricted area · CausalFunnel Admin
        </p>
      </div>
    </section>
  );
};

export default AdminLogin;
